import { Token } from '@module/user';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAccessToken } from '../utils/auth-guard.util';
import { getProfileFromLocalStorage } from '../utils/local-storage.util';

export type AuthProfile = {
  id?: string;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  image?: string;
  phoneNumber?: string;
};

const readProfile = (): AuthProfile | null => {
  const profile = getProfileFromLocalStorage();
  if (!profile || typeof profile !== 'object') {
    return null;
  }
  return profile as AuthProfile;
};

const evaluateTokenValidity = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const accessToken = getAccessToken();
  if (!accessToken) {
    return false;
  }

  if (Token.isTokenExpired(accessToken)) {
    localStorage.removeItem('access_token');
    return false;
  }

  return true;
};

const getTokenExpiryMs = (): number | null => {
  const accessToken = getAccessToken();
  if (!accessToken || Token.isTokenExpired(accessToken)) {
    return null;
  }

  return Token.decodeAccessToken(accessToken).exp * 1000;
};

export const useAuthentication = () => {
  const router = useRouter();
  const [isTokenValid, setIsTokenValid] = useState(() => evaluateTokenValidity());

  const syncAuthentication = useCallback(() => {
    const valid = evaluateTokenValidity();
    setIsTokenValid(valid);
    return valid ? getTokenExpiryMs() : null;
  }, []);

  useEffect(() => {
    const expiryMs = syncAuthentication();
    if (!expiryMs) {
      return;
    }

    const msUntilExpiry = expiryMs - Date.now();
    const timer = setTimeout(syncAuthentication, Math.max(msUntilExpiry, 0));

    return () => clearTimeout(timer);
  }, [syncAuthentication, router.asPath]);

  const profile = useMemo(() => (isTokenValid ? readProfile() : null), [isTokenValid]);

  return { profile, isTokenValid };
};
